// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import checkoutSharedStyles from "@conklin/App/SharedStyles/CheckoutStyles";
import Tooltip, { TooltipPresentationProps } from "@conklin/MobiusOverrides/Tooltip";
import IconNotificationTooltip from "@conklin/XDL/Icons/IconNotificationTooltip";
import { parseBoolean, Spacer } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    return {
        cart,
        // XNG-Changes
        billTo: getBillToState(state, cart?.billToId).value,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OnePageCheckoutConfirmationPaymentMethodStyles {
    container?: InjectableCss;
    paymentMethodTitleText?: TypographyPresentationProps;
    paymentMethodText?: TypographyPresentationProps;
    // XNG-Changes
    taxExmptionTooltip?: TooltipPresentationProps;
}

export const paymentMethodStyles: OnePageCheckoutConfirmationPaymentMethodStyles = {
    container: {
        css: css`
            flex-direction: column;
            margin-top: 15px;
        `,
    },
    paymentMethodTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
};

const styles = paymentMethodStyles;

const OnePageCheckoutConfirmationPaymentMethod = ({ cart, billTo }: Props) => {
    if (!cart) {
        return null;
    }
    // XNG-Changes
    const { creditCardBillingAddress, properties } = cart;
    // XNG-Changes: structure
    return (
        <>
            <Typography as="h3" {...checkoutSharedStyles.containerTitle}>
                {translate("Payment")}
            </Typography>
            <StyledWrapper {...checkoutSharedStyles.container}>
                <StyledWrapper {...checkoutSharedStyles.section}>
                    <Typography {...checkoutSharedStyles.sectionTitle}>{translate("Payment Method")}</Typography>
                    {properties.maskedCreditCardDetails ? (
                        <Typography {...checkoutSharedStyles.sectionValue}>
                            {properties.maskedCreditCardDetails}
                        </Typography>
                    ) : (
                        <Typography {...checkoutSharedStyles.sectionValue}>
                            {cart.paymentMethod?.description || cart.paymentMethod?.name}
                        </Typography>
                    )}
                    {creditCardBillingAddress && (
                        <>
                            <Spacer space={24} />
                            <Typography {...checkoutSharedStyles.sectionTitle}>
                                {translate("Credit Card Billing Address")}
                            </Typography>
                            {creditCardBillingAddress.address1 && (
                                <Typography {...checkoutSharedStyles.sectionValue}>
                                    {creditCardBillingAddress.address1}
                                </Typography>
                            )}
                            {creditCardBillingAddress.address2 && (
                                <Typography {...checkoutSharedStyles.sectionValue}>
                                    {creditCardBillingAddress.address2}
                                </Typography>
                            )}
                            <Typography {...checkoutSharedStyles.sectionValue}>
                                {`${creditCardBillingAddress.city}, ${creditCardBillingAddress.stateAbbreviation} ${creditCardBillingAddress.postalCode}`}
                            </Typography>
                            <Typography {...checkoutSharedStyles.sectionValue}>
                                {creditCardBillingAddress.countryAbbreviation}
                            </Typography>
                        </>
                    )}
                </StyledWrapper>
                <StyledWrapper {...checkoutSharedStyles.section}>
                    {!!cart.poNumber && (
                        <StyledWrapper>
                            <Typography
                                {...checkoutSharedStyles.sectionTitle}
                                // XNG-Changes
                                data-test-selector="orderConfirmation_PO-Title"
                            >
                                {translate("Purchase Order #")}
                            </Typography>
                            <Typography
                                {...checkoutSharedStyles.sectionValue}
                                // XNG-Changes
                                data-test-selector="orderConfirmation_PO-Number"
                            >
                                {cart.poNumber}
                            </Typography>
                        </StyledWrapper>
                    )}
                    {parseBoolean(cart.properties.isTaxExempt) && (
                        <>
                            <Spacer space={24} />
                            <StyledWrapper>
                                <Typography {...checkoutSharedStyles.sectionTitle}>
                                    {translate("Tax Exemption")}
                                </Typography>
                                <Typography {...checkoutSharedStyles.sectionValue}>
                                    {translate("Tax Exempt Status Is Pending")}
                                    <Tooltip
                                        text={siteMessage("Custom_CheckoutTaxEmptyTooltip_Message")}
                                        triggerComponent={<Icon size={16} src={IconNotificationTooltip} />}
                                        // XNG-Changes
                                        {...styles.taxExmptionTooltip}
                                    />
                                </Typography>
                            </StyledWrapper>
                        </>
                    )}
                    {!!cart.properties.taxExemptCertificateNumber && (
                        <>
                            <Spacer space={24} />
                            <StyledWrapper>
                                <Typography {...checkoutSharedStyles.sectionTitle}>
                                    {translate("Tax Exempt Certificate Number")}
                                </Typography>
                                <Typography {...checkoutSharedStyles.sectionValue}>
                                    {cart.properties.taxExemptCertificateNumber}
                                </Typography>
                            </StyledWrapper>
                        </>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OnePageCheckoutConfirmationPaymentMethod),
    definition: {
        displayName: "Payment Method",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
